/**
 * Admin surface for managing the performance-wallboard capability token.
 *
 * Shows the current active token's metadata (suffix + last-used) and
 * exposes a "Rotate token" action that mints a new credential. The
 * plaintext token is returned exactly once — the dialog after rotation
 * is the only place the operator ever sees it. Closing the dialog means
 * rotating again to recover access.
 *
 * Admin-only at v1 (Permit.WALLBOARD_MANAGE; see the wallboard module
 * spec). The page is mounted inside the SettingsLayout's admin-only
 * branch in `app/routes.tsx` so role gating happens at the route layer
 * rather than here.
 */

import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";

import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import type {
  WallboardRotateResult,
  WallboardTokenStatus,
} from "@shared-core/data/api-types";

import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";

export function SettingsWallboardPage(): JSX.Element {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();

  const [status, setStatus] = useState<WallboardTokenStatus | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [rotation, setRotation] = useState<WallboardRotateResult | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [copied, setCopied] = useState<"url" | "token" | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const next = await adapter.getWallboardTokenStatus();
      setStatus(next);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [adapter]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleRotate = useCallback(async () => {
    setIsRotating(true);
    try {
      const result = await adapter.rotateWallboardToken();
      setRotation(result);
      // Refresh the underlying status so the page's metadata block shows
      // the new suffix when the dialog is dismissed.
      await loadStatus();
    } catch {
      setPhase("error");
    } finally {
      setIsRotating(false);
    }
  }, [adapter, loadStatus]);

  const handleCopy = useCallback(
    async (kind: "url" | "token", value: string) => {
      await capabilities.copyToClipboard(value);
      setCopied(kind);
      // Plain setTimeout (not window.setTimeout) — keeps this file
      // free of bare browser globals, which the portability check
      // forbids in pages. Both forms resolve to the same DOM API in
      // the web shell, but the no-`window.` form is what the rule
      // wants to see.
      setTimeout(() => setCopied(null), 2000);
    },
    [capabilities],
  );

  const handleDismissRotation = useCallback(() => {
    setRotation(null);
    setCopied(null);
  }, []);

  if (phase === "loading") {
    return (
      <PageShell>
        <div className="text-muted-foreground">Loading…</div>
      </PageShell>
    );
  }

  if (phase === "error" || status === null) {
    return (
      <PageShell>
        <div className="text-destructive">
          Could not load the wallboard token status. Please refresh and try
          again.
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Card>
        <CardHeader>
          <CardTitle>Current token</CardTitle>
          <CardDescription>
            The single shared capability credential the on-site screens present
            in the wallboard URL.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status.has_active_token ? (
            <TokenMetadataBlock status={status} />
          ) : (
            <p className="text-muted-foreground">
              No token has been minted yet. Rotate to generate the first one.
            </p>
          )}
          <div className="mt-6 flex items-center gap-3">
            <Button
              type="button"
              onClick={handleRotate}
              disabled={isRotating}
              data-testid="wallboard-rotate-button"
            >
              {status.has_active_token ? "Rotate token" : "Generate token"}
            </Button>
            {status.has_active_token && (
              <p className="text-sm text-muted-foreground">
                Rotating immediately invalidates the previous token; every
                screen must be re-pasted with the new URL.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {rotation && (
        <RevealDialog
          rotation={rotation}
          copied={copied}
          onCopy={handleCopy}
          onDismiss={handleDismissRotation}
        />
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------- pieces ---

function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Wallboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage the access token for the on-site performance wallboard.
        </p>
      </header>
      {children}
    </div>
  );
}

function TokenMetadataBlock({
  status,
}: {
  status: WallboardTokenStatus;
}): JSX.Element {
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field
        label="Suffix"
        value={status.token_suffix ? `…${status.token_suffix}` : "—"}
      />
      <Field
        label="Created"
        value={formatTimestamp(status.created_at ?? null)}
      />
      <Field
        label="Last used"
        value={formatTimestamp(status.last_used_at ?? null)}
      />
      <Field
        label="Last used from"
        value={
          status.last_used_ip ?? "Never (no screen has used this token yet)"
        }
      />
    </dl>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function RevealDialog({
  rotation,
  copied,
  onCopy,
  onDismiss,
}: {
  rotation: WallboardRotateResult;
  copied: "url" | "token" | null;
  onCopy: (kind: "url" | "token", value: string) => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wallboard-rotation-title"
    >
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle id="wallboard-rotation-title">
            New wallboard token — copy now
          </CardTitle>
          <CardDescription>
            This is the only time the plaintext token is shown. Closing this
            dialog without copying means you'll need to rotate again to recover
            access. The previous token has already been invalidated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RevealField
            label="Wallboard URL"
            value={rotation.pasted_url}
            kind="url"
            copied={copied === "url"}
            onCopy={() => onCopy("url", rotation.pasted_url)}
            description="Paste this into each screen's display admin."
          />
          <RevealField
            label="Token"
            value={rotation.token}
            kind="token"
            copied={copied === "token"}
            onCopy={() => onCopy("token", rotation.token)}
            description="Only needed if the screen-display system requires the token separately from the URL."
          />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onDismiss}
              data-testid="wallboard-rotation-dismiss"
            >
              I've saved the URL
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RevealField({
  label,
  value,
  kind,
  copied,
  onCopy,
  description,
}: {
  label: string;
  value: string;
  kind: "url" | "token";
  copied: boolean;
  onCopy: () => void;
  description: string;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCopy}
          data-testid={`wallboard-copy-${kind}`}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <code className="mt-2 block break-all rounded-md bg-muted px-3 py-2 text-sm">
        {value}
      </code>
      <p className="mt-2 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return formatDateTimeOrDash(iso);
}
